import {
  ERROR_CODES,
  isErrorCode,
  type ErrorCode,
} from "@onsemble/shared/error-codes";
import { TanstackError } from "@/requests/client";

/**
 * Normalized view of any error thrown by the app's two request stacks:
 * - typed stack (openapi-fetch): TanstackError carrying the parsed error body
 * - legacy stack (apiRequest/getQueryFn): Error with a "STATUS: rawtext" message
 * plus raw fetch failures (TypeError) and anything else.
 */
export interface ParsedApiError {
  /** HTTP status, or 0 when no response was received. */
  status: number;
  /** Registry code from the error envelope, when the server sent one. */
  code: ErrorCode | null;
  /** Best available human-readable message (the server's when present). */
  message: string;
  /** Request-scoped id, for correlating a user report with logs/Sentry. */
  requestId?: string;
  /** Whether the request may be safely retried. */
  retryable: boolean;
  /** The request never reached the server (offline, DNS failure, timeout). */
  isNetworkError: boolean;
}

/**
 * Placeholder used when no usable message could be extracted. Exported so
 * userErrorMessage can tell "server-provided message" apart from this filler.
 */
export const GENERIC_ERROR_MESSAGE = "Something went wrong. Please try again.";

const NETWORK_MESSAGE = "Network error";

/** Matches legacy `throw new Error(`${status}: ${text}`)` from queryClient. */
const LEGACY_STATUS_PATTERN = /^(\d{3}):\s*([\s\S]*)$/;

/** Browser fetch-failure and timeout messages (Chrome/Firefox/Safari). */
const NETWORK_FAILURE_PATTERN =
  /Failed to fetch|NetworkError|Load failed|network|timeout/i;

interface EnvelopeFields {
  message?: string;
  code?: ErrorCode;
  requestId?: string;
  retryable?: boolean;
}

/** Pull whatever envelope/legacy fields exist off a parsed error body
 * ({success, error, code, requestId, retryable} or just {error}). */
function extractEnvelope(body: unknown): EnvelopeFields {
  if (typeof body !== "object" || body === null) return {};
  const record = body as Record<string, unknown>;
  const fields: EnvelopeFields = {};
  if (typeof record.error === "string" && record.error) {
    fields.message = record.error;
  } else if (typeof record.message === "string" && record.message) {
    fields.message = record.message;
  }
  if (isErrorCode(record.code)) fields.code = record.code;
  if (typeof record.requestId === "string" && record.requestId) {
    fields.requestId = record.requestId;
  }
  if (typeof record.retryable === "boolean") fields.retryable = record.retryable;
  return fields;
}

function networkError(message?: string): ParsedApiError {
  return {
    status: 0,
    code: null,
    message: message || NETWORK_MESSAGE,
    retryable: true,
    isNetworkError: true,
  };
}

function fromEnvelope(
  status: number,
  env: EnvelopeFields,
  fallbackMessage?: string,
): ParsedApiError {
  const code = env.code ?? null;
  return {
    status,
    code,
    message: env.message || fallbackMessage || GENERIC_ERROR_MESSAGE,
    requestId: env.requestId,
    retryable: env.retryable ?? (code ? ERROR_CODES[code].retryable : false),
    isNetworkError: false,
  };
}

/** Normalize any thrown value from an API call into a ParsedApiError. */
export function parseApiError(e: unknown): ParsedApiError {
  if (e instanceof TanstackError) {
    // Status 0 means the fetch itself failed (handleClientMethod's catch,
    // or post-photo's NETWORK_ERROR/UPLOAD_INTERRUPTED sentinels).
    if (e.status === 0) return networkError(e.error);
    const env = extractEnvelope(e.body);
    return fromEnvelope(e.status, env, e.error);
  }

  if (e instanceof Error) {
    // Legacy stack: "STATUS: rawtext" where rawtext is the response body.
    const match = LEGACY_STATUS_PATTERN.exec(e.message);
    if (match) {
      const status = Number(match[1]);
      const raw = match[2].trim();
      let env: EnvelopeFields = {};
      try {
        env = extractEnvelope(JSON.parse(raw));
      } catch {
        // Body wasn't JSON — use the raw text unless it's an HTML error page.
        if (raw && !raw.startsWith("<")) env = { message: raw };
      }
      return fromEnvelope(status, env);
    }

    if (e instanceof TypeError || NETWORK_FAILURE_PATTERN.test(e.message)) {
      return networkError();
    }

    return {
      status: 0,
      code: null,
      message: e.message || GENERIC_ERROR_MESSAGE,
      retryable: false,
      isNetworkError: false,
    };
  }

  return {
    status: 0,
    code: null,
    message: GENERIC_ERROR_MESSAGE,
    retryable: false,
    isNetworkError: false,
  };
}
